// Local BS5 modal renderer for v6 portal-mount modals (mode 1 / mode
// 3 fallback of openPortalModal). Counterpart to the embed script's
// renderModal iframe path, framed in BS5 instead of dialog.js so the
// donate widget's React content portals into a styled box that
// matches the surrounding v6 chrome.
//
// Per-id cache: a modal opened once stays in the DOM so a reopen
// reuses the same element (matching renderModal's modalCache shape).
// React state inside the picker is owned by the picker component —
// not reset here — so filter input survives close → reopen unless
// the picker chooses to reset on value-prop change.
//
// Sizing opts (maxWidth, scrollWithin, clipContent) mirror what
// renderModal applies on the iframe path so the modal looks the same
// across modes. maxWidth flows through BS5's --bs-modal-width custom
// property (the dialog's own sm+ max-width source); scrollWithin
// toggles the modal-dialog-scrollable class which gives modal-body
// internal scrolling; clipContent toggles overflow-hidden on
// modal-content so child backgrounds / rows clip to the rounded
// corners (BS5 modal-content doesn't clip by default — unlike v7's
// widget-modal-body which already has overflow:hidden). Re-applied
// on cache hit in case a caller ever changes the shape mid-session —
// matches renderModal's behaviour.
//
// Returned shape `{ target, close }` matches openPortalModal's
// contract — the donate widget code calls `close()` to programmatically
// dismiss after a value is picked; the modal's own backdrop/ESC paths
// close via BS5's native behavior. We don't surface a backdrop-close
// event today; if the picker grows a need to react to external
// dismissal, wire up a `hidden.bs.modal` listener here.

const cache = new Map()

const applyOptions = (element, { maxWidth, scrollWithin, clipContent }) => {
  const dialog = element.querySelector('.modal-dialog')
  dialog.classList.toggle('modal-dialog-scrollable', !!scrollWithin)
  element.querySelector('.modal-content')
    .classList.toggle('overflow-hidden', !!clipContent)
  if (maxWidth) dialog.style.setProperty('--bs-modal-width', maxWidth)
  else dialog.style.removeProperty('--bs-modal-width')
}

export const injectLocalPortalModal = ({ id, ariaLabel, maxWidth, scrollWithin, clipContent }) => {
  const cached = cache.get(id)
  if (cached) {
    applyOptions(cached.element, { maxWidth, scrollWithin, clipContent })
    bootstrap.Modal.getOrCreateInstance(cached.element).show()
    return { target: cached.target, close: cached.close }
  }

  const element = document.createElement('div')
  element.className = 'modal fade'
  element.tabIndex = -1
  if (ariaLabel) element.setAttribute('aria-label', ariaLabel)
  // Empty modal-content is the portal target — the picker provides
  // its own modal-header (title + close button + search) and modal-
  // body (scrollable list), matching the markup the iframe path uses
  // inside the renderModal dialog.
  element.innerHTML =
    '<div class="modal-dialog">'
    + '<div class="modal-content" data-plg-portal-target></div>'
    + '</div>'
  document.body.appendChild(element)
  applyOptions(element, { maxWidth, scrollWithin, clipContent })

  const target = element.querySelector('[data-plg-portal-target]')
  const bsModal = new bootstrap.Modal(element)
  bsModal.show()

  const close = () => bsModal.hide()
  cache.set(id, { element, target, close })
  return { target, close }
}
